import {
    API,
    graphqlOperation
} from "aws-amplify";
import {
    CreateAttachmentMutation,
    GetAttachmentQuery,
    ListAttachmentsByParentQuery,
    ListAttachmentsByParentQueryVariables,
    ListAttachmentsByPropertyQuery,
    UpdateAttachmentMutation
} from "../../../../API";
import { 
    getAttachment, 
    listAttachmentsByParent,
    listAttachmentsByProperty
} from "../../../../graphql/queries";

import { Attachment } from "../../../../models";
import AttachmentRecordAlreadyExistsError from "../errors/AttachmentRecordAlreadyExistsError";
import AttachmentRecordDAO from "../AttachmentRecordDAO";
import AttachmentRecordMetricNames from "../AttachmentRecordMetricNames";
import ClientLogger from "../../../logging/ClientLogger";
import CreateAttachmentRecordError from "../errors/CreateAttachmentRecordError";
import GetAttachmentRecordByIdError from "../errors/GetAttachmentRecordByIdError";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import { IDTokenSupplier } from "../../../auth/IDTokenSupplier";
import { createAttachment } from "../../../../graphql/mutations";
import { updateAttachment } from "../../../../graphql/mutations";
import ListAttachmentRecordsByPropertyIdError from "../errors/ListAttachmentRecordsByPropertyIdError";
import { ListResponse } from "../../../util/data/list/ListResponse";
import UpdateAttachmentRecordError from "../errors/UpdateAttachmentRecordError";

export default class GraphQLAttachmentRecordDAO implements AttachmentRecordDAO {
    private readonly logger: ClientLogger;
    private readonly gqlOperation: typeof graphqlOperation;
    private readonly api: typeof API;
    private readonly idTokenSupplier: IDTokenSupplier;

    constructor(
        logger: ClientLogger,
        api: typeof API,
        gqlOperation: typeof graphqlOperation,
        idTokenSupplier: IDTokenSupplier
    ) {
        this.logger = logger;
        this.api = api;
        this.gqlOperation = gqlOperation;
        this.idTokenSupplier = idTokenSupplier;
    }

    public async create(
        record: Attachment
    ): Promise<Attachment> {
        this.logger.info(
            `Creating Attachment Record: ${record.id}`,
            record,
            [AttachmentRecordMetricNames.CREATE_ATTEMPT]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const result: GraphQLResult<CreateAttachmentMutation> = await this.api.graphql(
                this.gqlOperation(
                    createAttachment,
                    { input: record },
                    authToken
                )
            ) as GraphQLResult<CreateAttachmentMutation>;
            return result.data?.createAttachment!;
        } catch (error) {
            if ((error as any)?.errors && (error as any)?.errors[0].errorType === "ConditionalCheckFailedException") {
                const errorMessage = `Attachment record already exists: ${record.id}`;
                this.logger.warn(
                    errorMessage,
                    error,
                    [AttachmentRecordMetricNames.CREATE_FAILURE]
                );
                throw new AttachmentRecordAlreadyExistsError(
                    errorMessage
                );
            }
            const errorMessage = `Unable to create Attachment Record: ${record.id}`;
            this.logger.error(
                errorMessage,
                error,
                [AttachmentRecordMetricNames.CREATE_FAILURE]
            );
            throw new CreateAttachmentRecordError(errorMessage, {
                cause: error as Error
            });
        }
    }
    
    public async update(
        attachmentId: string,
        updatedAttachment: Attachment
    ): Promise<Attachment> {
        this.logger.info(
            `Updating Attachment Record: ${attachmentId}`,
            updatedAttachment,
            [AttachmentRecordMetricNames.UPDATE_ATTEMPT]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const result: GraphQLResult<UpdateAttachmentMutation> = await this.api.graphql(
                this.gqlOperation(
                    updateAttachment,
                    { input: { attachmentId, ...updatedAttachment } },
                    authToken
                )
            ) as GraphQLResult<UpdateAttachmentMutation>;
            return result.data?.updateAttachment!;
        } catch (error) {
            const errorMessage = `Unable to update Attachment Record: ${attachmentId}`;
            this.logger.error(errorMessage, error, [AttachmentRecordMetricNames.UPDATE_FAILURE]);
            throw new UpdateAttachmentRecordError(errorMessage, error as Error);
        }
    }

    public async delete(
        id: string
    ): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public async getById(
        id: string
    ): Promise<Attachment> {
        this.logger.info(
            `Getting Attachment Record: ${id}`,
            undefined,
            [AttachmentRecordMetricNames.GET_BY_ID_ATTEMPT]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const queryResult = await this.api.graphql(
                this.gqlOperation(
                    getAttachment,
                    { id },
                    authToken
                )
            ) as GraphQLResult<GetAttachmentQuery>;
            const result = queryResult.data?.getAttachment as Attachment;
            if (!result) {
                throw new Error("Attachment Record not found");
            }
            return result;
        } catch (error) {
            const errorMessage: string = `Unable to get Attachment Record: ${id}`;
            this.logger.error(
                errorMessage,
                error,
                [AttachmentRecordMetricNames.GET_BY_ID_FAILURE]
            );
            throw new GetAttachmentRecordByIdError(errorMessage, error as Error);
        }
    }

    public async listByParentId(parentId: string): Promise<Attachment[]> {
        this.logger.info(
            `Getting Attachment Records with parentId: ${parentId}`,
            undefined,
            [AttachmentRecordMetricNames.LIST_BY_PARENT_ID_ATTEMPT]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const queryResult = await this.api.graphql(
                this.gqlOperation(
                    listAttachmentsByParent,
                    {
                        parentId,
                        filter: {
                            _deleted: {
                                ne: true
                            }
                        }
                    } satisfies ListAttachmentsByParentQueryVariables,
                    authToken
                )
            ) as GraphQLResult<ListAttachmentsByParentQuery>;
            const result = queryResult.data?.listAttachmentsByParent?.items! as Attachment[];
            if (!result) {
                throw new Error(`Attachment Records not found`);
            }
            return result;
        } catch (error) {
            const errorMessage: string = `Unable to get Attachment Records with parentId: ${parentId}`;
            this.logger.error(
                errorMessage,
                error,
                [AttachmentRecordMetricNames.LIST_BY_PARENT_ID_FAILURE]
            );
            throw new GetAttachmentRecordByIdError(errorMessage, error as Error);
        }
    }

    public async listByPropertyId(propertyId: string, nextToken?: string): Promise<ListResponse<Attachment>> {
        this.logger.info(
            `Getting Attachment Records with propertyId: ${propertyId}`,
            undefined,
            [AttachmentRecordMetricNames.LIST_BY_PROPERTY_ID_ATTEMPT]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const queryResult = await this.api.graphql(
                this.gqlOperation(
                    listAttachmentsByProperty,
                    { propertyId, nextToken},
                    authToken,
                )
            ) as GraphQLResult<ListAttachmentsByPropertyQuery>;
            const result = queryResult.data?.listAttachmentsByProperty?.items! as Attachment[];
            if (!result) {
                throw new Error(`Attachment Records not found`);
            }
            return {
                items: result,
                nextToken: queryResult.data?.listAttachmentsByProperty?.nextToken ?? null
            };
        } catch (error) {
            const errorMessage: string = `Unable to get Attachment Records with propertyId: ${propertyId}`;
            this.logger.error(
                errorMessage,
                error,
                []
            );
            throw new ListAttachmentRecordsByPropertyIdError(errorMessage, error as Error);
        }
    }

    public async listMostRecentByParentId(
        parentId: string
    ): Promise<Attachment[]> {
        throw new Error("Method not implemented.");
    }

    public async listMostRecentByParentIdWithLimit(
        parentId: string,
        limit: number
    ): Promise<Attachment[]> {
        throw new Error("Method not implemented.");
    }
}
